'use client';

import { useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { useEffect, useState } from 'react';

import { WistiaPlaceholder } from './WistiaPlaceholder';

type LoadedWistiaVideo = {
  remove: () => void;
  time: (time: number) => Promise<void>;
  container: HTMLDivElement;
};

type WistiaExternalProps = {
  id: string;
  name?: string;
};

export const WistiaExternal = ({ id, name }: WistiaExternalProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);

  const [loadedVideo, setLoadedVideo] = useState<LoadedWistiaVideo | null>(
    null
  );

  const searchParams = useSearchParams();

  const paramsTime = searchParams?.get('wtime');

  const paramsId = searchParams?.get('wid');

  useEffect(() => {
    if ('_wq' in window && isLoaded) {
      window._wq.push({
        id: id,
        onReady: (video: LoadedWistiaVideo) => {
          setLoadedVideo(video);
        },
      });
    }
  }, [id, isLoaded]);

  useEffect(() => {
    const pass =
      typeof loadedVideo?.time === 'function' && paramsTime && paramsId === id;

    if (!pass) {
      return;
    }

    loadedVideo.time(Number(paramsTime));
    loadedVideo.container.scrollIntoView({ block: 'center' });
  }, [loadedVideo, paramsTime, paramsId, id]);

  useEffect(() => {
    return () => {
      if (loadedVideo?.remove) {
        loadedVideo.remove();
      }
    };
  }, [loadedVideo]);

  useEffect(() => {
    if (!loadedVideo || !name) {
      return;
    }

    const container = loadedVideo.container;

    const images = container.getElementsByTagName('img');

    Array.from(images).forEach((el) => {
      el.setAttribute('alt', name);
    });
  }, [loadedVideo, name]);

  if (isError) {
    return (
      <iframe
        src={`https://fast.wistia.net/embed/iframe/${id}?videoFoam=true&seo=false`}
        title='Video player'
        allow='autoplay; fullscreen'
        className='wistia_embed'
        name='wistia_embed'
        allowFullScreen
        width='100%'
        height='100%'
      />
    );
  }

  return (
    <>
      <Script
        src='https://fast.wistia.com/assets/external/E-v1.js'
        onLoad={() => {
          setIsLoaded(true);
        }}
        onError={() => {
          setIsError(true);
        }}
        async
      />
      <div
        className={`wistia_embed wistia_async_${id} w-full overflow-hidden relative h-full videoFoam=true seo=false`}
      >
        <WistiaPlaceholder id={id} name={name} />
      </div>
    </>
  );
};
